import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Building2, Pencil, Trash2, Link2, Unlink, ChevronRight, Palette, Eye, EyeOff, Users, Mail, Copy, Check, RefreshCw, X, Clock, Send, LinkIcon, UserPlus, Shield, ChevronDown } from 'lucide-react';

import { API_BASE as API } from '../config';

export default function Organizations() {
  const { user, operatorContext } = useAuth();
  // Treat operator-impersonating-a-Provider as a Provider session for the
  // purpose of this page. The user JWT carries `account_type: null` for
  // platform operators — the impersonated tenant's type lives on
  // `operatorContext.accountType`. Without this, operators see the
  // "Upgrade to Provider" prompt even when they're inside a V2 tenant.
  const isProviderSession = user?.account_type === 'provider'
    || (user?.is_platform_operator && operatorContext?.accountType === 'provider');
  const [orgs, setOrgs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [formData, setFormData] = useState({ name: '', logo_url: '', branding_color: '#2563eb' });
  const [members, setMembers] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  // Invite state (provider-level)
  const [showInvite, setShowInvite] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [invites, setInvites] = useState([]);
  const [copiedLink, setCopiedLink] = useState(false);
  const [activeInviteTab, setActiveInviteTab] = useState('link');
  const [emailForm, setEmailForm] = useState({ email: '', company_name: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Organizations page always needs the FULL cross-org view (every
      // company, assigned or not). Pass `skipOrgFilter: true` so the global
      // axios interceptor in AuthContext doesn't auto-inject `?org_id=` for
      // /api/companies when the user has a specific org selected in the nav.
      const [orgRes, compRes, usersRes] = await Promise.all([
        axios.get(`${API}/organizations`),
        axios.get(`${API}/companies`, { skipOrgFilter: true }),
        axios.get(`${API}/users`),
      ]);
      setOrgs(orgRes.data);
      setCompanies(compRes.data);
      setAllUsers(usersRes.data);
    } catch (err) {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Org CRUD ──
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/organizations`, formData);
      toast.success(`Organization "${formData.name}" created`);
      setShowCreate(false);
      setFormData({ name: '', logo_url: '', branding_color: '#2563eb' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create organization');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/organizations/${editOrg.org_id}`, formData);
      toast.success('Organization updated');
      setEditOrg(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`Delete "${org.name}"? Companies and claims will be unlinked but not deleted.`)) return;
    try {
      await axios.delete(`${API}/organizations/${org.org_id}`);
      toast.success(`"${org.name}" deleted`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleAssign = async (orgId, companyName) => {
    try {
      await axios.post(`${API}/organizations/${orgId}/assign-company`, { company_name: companyName });
      toast.success(`"${companyName}" assigned`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleUnassign = async (orgId, companyName) => {
    try {
      await axios.post(`${API}/organizations/${orgId}/unassign-company`, { company_name: companyName });
      toast.success(`"${companyName}" removed`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleToggleVisibility = async (org) => {
    try {
      await axios.put(`${API}/organizations/${org.org_id}`, { data_visible: !org.data_visible });
      toast.success(`"${org.name}" is now ${!org.data_visible ? 'visible' : 'hidden'} in dashboards`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update visibility');
    }
  };

  const handleRemoveTenant = async (orgId, tenantId, tenantName) => {
    if (!window.confirm(`Remove "${tenantName}" from this organization? Their data will be unlinked and billing will revert.`)) return;
    try {
      await axios.post(`${API}/organizations/${orgId}/remove-tenant`, { tenant_id: tenantId });
      toast.success(`"${tenantName}" has been removed`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove tenant');
    }
  };

  const fetchMembers = async (orgId) => {
    try {
      const res = await axios.get(`${API}/organizations/${orgId}/scoped-members`);
      setMembers(prev => ({ ...prev, [orgId]: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load members');
    }
  };

  const handleAddMember = async (orgId, userId) => {
    try {
      await axios.post(`${API}/organizations/${orgId}/scoped-members`, { user_id: userId });
      toast.success('Member added');
      fetchMembers(orgId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (orgId, userId) => {
    try {
      await axios.delete(`${API}/organizations/${orgId}/scoped-members/${userId}`);
      toast.success('Member removed');
      fetchMembers(orgId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  // ── Provider-level invite functions ──
  const fetchInviteLink = async () => {
    try {
      const res = await axios.get(`${API}/invite-link`);
      setInviteLink(res.data);
    } catch (err) {
      toast.error('Failed to get invite link');
    }
  };

  const regenerateLink = async () => {
    try {
      const res = await axios.post(`${API}/invite-link/regenerate`);
      setInviteLink(prev => ({ ...prev, invite_code: res.data.invite_code }));
      toast.success('Invite link regenerated');
    } catch (err) {
      toast.error('Failed to regenerate link');
    }
  };

  const toggleRequireApproval = async () => {
    if (!inviteLink) return;
    try {
      const res = await axios.put(`${API}/require-approval`, { require_approval: !inviteLink.require_approval });
      setInviteLink(prev => ({ ...prev, require_approval: res.data.require_approval }));
      toast.success(res.data.require_approval ? 'Approval required for new joiners' : 'Auto-accept enabled');
    } catch (err) {
      toast.error('Failed to update setting');
    }
  };

  const copyInviteLink = () => {
    if (!inviteLink?.invite_code) return;
    navigator.clipboard.writeText(`${window.location.origin}/join/${inviteLink.invite_code}`);
    setCopiedLink(true);
    toast.success('Invite link copied!');
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const fetchInvites = async () => {
    try {
      const res = await axios.get(`${API}/invites`);
      setInvites(res.data);
    } catch (err) {
      toast.error('Failed to load invites');
    }
  };

  const handleSendEmailInvite = async () => {
    if (!emailForm.email) { toast.error('Email is required'); return; }
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/invite`, emailForm);
      toast.success(res.data.email_sent ? `Invite sent to ${emailForm.email}` : 'Invite created (email delivery pending)');
      setEmailForm({ email: '', company_name: '' });
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleInviteAction = async (inviteId, action) => {
    try {
      await axios.put(`${API}/invites/${inviteId}`, { action });
      toast.success(`Invite ${action}d`);
      fetchInvites();
      fetchData(); // Refresh orgs since approve creates a new org
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/invites/${inviteId}`);
      toast.success('Invite revoked');
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  const openInviteSection = () => {
    setShowInvite(true);
    setShowCreate(false);
    setEditOrg(null);
    if (!inviteLink) fetchInviteLink();
    fetchInvites();
  };

  const unassignedCompanies = companies.filter(c => !c.org_id);

  if (!isProviderSession) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 sm:p-8" data-testid="orgs-upgrade-prompt">
        <div className="max-w-xl mx-auto text-center py-20">
          <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Organizations</h2>
          <p className="text-gray-500 mb-6">Manage multiple organizations with the Provider plan.</p>
          <p className="text-sm text-gray-400">Current plan: <span className="font-semibold text-gray-600 capitalize">{user?.account_type || 'Solo'}</span></p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const pendingInvites = invites.filter(i => i.status === 'pending');
  const processedInvites = invites.filter(i => i.status !== 'pending');

  /**
   * Org create/edit form, rendered inline (not as a nested component).
   *
   * IMPORTANT: We used to define this as `const OrgForm = (...) => (...)` inside
   * the Organizations() body. That created a fresh component identity on every
   * render, so React unmounted/remounted the inputs on every keystroke and
   * focus was lost after one character. Inlining the JSX (or hoisting to a
   * module-scoped component) is the fix.
   */
  const renderOrgForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="bg-white rounded-lg border p-4 sm:p-6 mb-6 space-y-4" data-testid="org-form">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
        <input type="text" value={formData.name} onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required data-testid="org-name-input" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
          <input type="url" value={formData.logo_url} onChange={e => setFormData(prev => ({ ...prev, logo_url: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://..." data-testid="org-logo-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Brand Color</label>
          <div className="flex items-center gap-2">
            <input type="color" value={formData.branding_color} onChange={e => setFormData(prev => ({ ...prev, branding_color: e.target.value }))} className="w-10 h-10 rounded border cursor-pointer" data-testid="org-color-input" />
            <input type="text" value={formData.branding_color} onChange={e => setFormData(prev => ({ ...prev, branding_color: e.target.value }))} className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono" />
          </div>
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="org-submit-btn">{submitLabel}</button>
        <button type="button" onClick={() => { setShowCreate(false); setEditOrg(null); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">Cancel</button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8" data-testid="organizations-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="orgs-title">Organizations</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your organizations and invite companies to join.</p>
          </div>
          {!showCreate && !editOrg && (
            <div className="flex items-center gap-2">
              <button
                onClick={openInviteSection}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showInvite ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100'}`}
                data-testid="invite-org-btn"
              >
                <UserPlus className="w-4 h-4" /> Invite Organization
              </button>
              <button
                onClick={() => { setShowCreate(true); setShowInvite(false); setFormData({ name: '', logo_url: '', branding_color: '#2563eb' }); }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                data-testid="add-org-btn"
              >
                <Plus className="w-4 h-4" /> New Organization
              </button>
            </div>
          )}
        </div>

        {/* ── Invite Section (inline, above org list) ── */}
        {showInvite && (
          <div className="bg-white rounded-lg border mb-6 overflow-hidden" data-testid="invite-section">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-teal-600" />
                <h3 className="text-sm font-semibold text-gray-900">Invite Organization</h3>
              </div>
              <button onClick={() => setShowInvite(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded" data-testid="close-invite">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              {/* Tab switcher */}
              <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5 mb-4">
                {[
                  { key: 'link', icon: LinkIcon, label: 'Invite Link' },
                  { key: 'email', icon: Mail, label: 'Email Invite' },
                  { key: 'history', icon: Clock, label: 'History', badge: pendingInvites.length },
                ].map(t => (
                  <button
                    key={t.key}
                    onClick={() => { setActiveInviteTab(t.key); if (t.key === 'history') fetchInvites(); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeInviteTab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    data-testid={`invite-tab-${t.key}`}
                  >
                    <t.icon className="w-3.5 h-3.5" /> {t.label}
                    {t.badge > 0 && <span className="ml-1 bg-amber-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{t.badge}</span>}
                  </button>
                ))}
              </div>

              {/* Link Tab */}
              {activeInviteTab === 'link' && (
                <div className="space-y-3">
                  {inviteLink ? (
                    <>
                      <p className="text-xs text-gray-500">Share this link with organizations you want to invite. When they accept, a new organization will be created automatically.</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-50 border rounded-lg px-3 py-2.5 text-xs font-mono text-gray-600 truncate" data-testid="invite-link-display">
                          {window.location.origin}/join/{inviteLink.invite_code}
                        </div>
                        <button
                          onClick={copyInviteLink}
                          className={`flex items-center gap-1 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${copiedLink ? 'bg-green-100 text-green-700' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
                          data-testid="copy-invite-link"
                        >
                          {copiedLink ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                        </button>
                        <button onClick={regenerateLink} className="p-2.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100" title="Regenerate link" data-testid="regenerate-link">
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>Require approval before joining</span>
                        </div>
                        <button
                          onClick={toggleRequireApproval}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${inviteLink.require_approval ? 'bg-teal-500' : 'bg-gray-300'}`}
                          data-testid="toggle-approval"
                        >
                          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform ${inviteLink.require_approval ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-400">
                        {inviteLink.require_approval
                          ? 'Anyone with this link can request to join. You must approve each request.'
                          : 'Anyone with this link can join automatically. A new organization will be created for them.'}
                      </p>
                    </>
                  ) : (
                    <div className="text-xs text-gray-400 text-center py-3">Loading invite link...</div>
                  )}
                </div>
              )}

              {/* Email Tab */}
              {activeInviteTab === 'email' && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">Send a direct email invite. When accepted, a new organization will be created automatically.</p>
                  <div className="space-y-2">
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-3.5 w-3.5 text-gray-400" />
                      <input type="email" placeholder="Email address *" value={emailForm.email} onChange={(e) => setEmailForm(prev => ({ ...prev, email: e.target.value }))} className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm" data-testid="invite-email-input" />
                    </div>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-2.5 h-3.5 w-3.5 text-gray-400" />
                      <input type="text" placeholder="Company / Organization name (optional)" value={emailForm.company_name} onChange={(e) => setEmailForm(prev => ({ ...prev, company_name: e.target.value }))} className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm" data-testid="invite-company-input" />
                    </div>
                  </div>
                  <button onClick={handleSendEmailInvite} disabled={sendingEmail || !emailForm.email} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="send-email-invite">
                    <Send className="w-3.5 h-3.5" /> {sendingEmail ? 'Sending...' : 'Send Invite'}
                  </button>
                  <p className="text-[11px] text-gray-400">The recipient will receive an email with a link to create an account. Email invites skip the approval step.</p>
                </div>
              )}

              {/* History Tab */}
              {activeInviteTab === 'history' && (
                <div className="space-y-2">
                  {pendingInvites.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-[11px] font-semibold text-amber-600 uppercase tracking-wider">Pending ({pendingInvites.length})</p>
                      {pendingInvites.map(inv => (
                        <InviteRow key={inv.invite_id} inv={inv} onAction={handleInviteAction} onRevoke={handleRevokeInvite} />
                      ))}
                    </div>
                  )}
                  {processedInvites.length > 0 && (
                    <div className="space-y-1.5 mt-3">
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Processed ({processedInvites.length})</p>
                      {processedInvites.slice(0, 10).map(inv => (
                        <InviteRow key={inv.invite_id} inv={inv} onAction={handleInviteAction} onRevoke={handleRevokeInvite} />
                      ))}
                    </div>
                  )}
                  {invites.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-4">No invites sent yet. Use the Link or Email tab to invite organizations.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create form */}
        {showCreate && renderOrgForm(handleCreate, 'Create Organization')}

        {/* Edit form */}
        {editOrg && renderOrgForm(handleUpdate, 'Save Changes')}

        {/* Org list */}
        {orgs.length === 0 && !showCreate ? (
          <div className="text-center py-16 bg-white rounded-lg border" data-testid="orgs-empty">
            <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">No organizations yet.</p>
            <p className="text-sm text-gray-400">Create one manually or invite an organization to join.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orgs.map(org => {
              const isExpanded = expandedOrg === org.org_id;
              const orgCompanies = companies.filter(c => c.org_id === org.org_id);
              return (
                <div key={org.org_id} className="bg-white rounded-lg border overflow-hidden" data-testid={`org-card-${org.org_id}`}>
                  {/* Org header */}
                  <div
                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedOrg(isExpanded ? null : org.org_id)}
                    data-testid={`org-header-${org.org_id}`}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: org.branding_color || '#2563eb' }}>
                      {org.logo_url ? <img src={org.logo_url} alt="" className="w-8 h-8 rounded object-cover" /> : <Building2 className="w-5 h-5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{org.name}</h3>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500">{org.company_count} companies &middot; {org.claim_count} claims</p>
                        {org.linked_tenant_name && (
                          <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-medium" data-testid={`linked-badge-${org.org_id}`}>
                            Linked: {org.linked_tenant_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {org.branding_color && (
                        <span className="hidden sm:flex items-center gap-1 text-xs text-gray-400 mr-2"><Palette className="w-3 h-3" />{org.branding_color}</span>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); setEditOrg(org); setShowCreate(false); setShowInvite(false); setFormData({ name: org.name, logo_url: org.logo_url || '', branding_color: org.branding_color || '#2563eb' }); }} className="p-1.5 text-gray-400 hover:text-blue-600 rounded" data-testid={`org-edit-${org.org_id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(org); }} className="p-1.5 text-gray-400 hover:text-red-600 rounded" data-testid={`org-delete-${org.org_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="border-t bg-gray-50 p-4 space-y-5" data-testid={`org-details-${org.org_id}`}>
                      {/* Visibility */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Data Visibility</p>
                          <p className="text-xs text-gray-400 mt-0.5">{org.data_visible ? "This org's data appears in the provider dashboard." : "Hidden from the provider dashboard."}</p>
                        </div>
                        <button onClick={() => handleToggleVisibility(org)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${org.data_visible ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`} data-testid={`org-visibility-${org.org_id}`}>
                          {org.data_visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                          {org.data_visible ? 'Visible' : 'Hidden'}
                        </button>
                      </div>

                      {/* Linked Tenant */}
                      {org.linked_tenant_id && (
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4" data-testid={`linked-tenant-${org.org_id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-teal-600" />
                              <p className="text-sm font-semibold text-teal-800">Linked Organization</p>
                            </div>
                            <button onClick={() => handleRemoveTenant(org.org_id, org.linked_tenant_id, org.linked_tenant_name)} className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-red-100 text-red-700 rounded hover:bg-red-200" data-testid={`remove-tenant-${org.org_id}`}>
                              <Unlink className="w-3 h-3" /> Remove Tenant
                            </button>
                          </div>
                          <p className="text-sm text-teal-700">{org.linked_tenant_name || 'Unknown'}</p>
                          <p className="text-[11px] text-teal-500 mt-1">This tenant's companies and claims are visible in this organization. Their billing is managed under your plan.</p>
                        </div>
                      )}

                      {/* Assigned companies */}
                      {orgCompanies.length > 0 ? (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned Companies ({orgCompanies.length})</p>
                          {orgCompanies.map(c => (
                            <div key={c.name} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border">
                              <span className="text-sm text-gray-800">{c.name}</span>
                              <button onClick={() => handleUnassign(org.org_id, c.name)} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700" data-testid={`unassign-${c.name}`}>
                                <Unlink className="w-3 h-3" /> Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Assigned Companies</p>
                          <p className="text-sm text-gray-400">No companies assigned yet.</p>
                        </div>
                      )}

                      {/* Assign unassigned */}
                      {unassignedCompanies.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Available Companies</p>
                          <div className="flex flex-wrap gap-2">
                            {unassignedCompanies.map(c => (
                              <button key={c.name} onClick={() => handleAssign(org.org_id, c.name)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors" data-testid={`assign-${c.name}`}>
                                <Link2 className="w-3 h-3" /> {c.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Members */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Members</p>
                          <div className="flex items-center gap-2">
                            {members[org.org_id] && (
                              <span className="text-[10px] text-gray-400">{members[org.org_id].length} total</span>
                            )}
                            {!members[org.org_id] && (
                              <button onClick={() => fetchMembers(org.org_id)} className="text-xs text-blue-600 hover:text-blue-800" data-testid={`load-members-${org.org_id}`}>
                                <Users className="w-3 h-3 inline mr-1" />Load Members
                              </button>
                            )}
                          </div>
                        </div>
                        {members[org.org_id] && (
                          <div className="space-y-2">
                            {members[org.org_id].map(m => (
                              <div key={m.user_id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border" data-testid={`member-row-${m.user_id}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                  {m.picture ? (
                                    <img src={m.picture} alt="" className="w-6 h-6 rounded-full" />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-medium text-gray-600 shrink-0">{m.name?.[0]?.toUpperCase() || '?'}</div>
                                  )}
                                  <div className="min-w-0">
                                    <span className="text-sm text-gray-800">{m.name || m.email}</span>
                                    <span className="text-xs text-gray-400 ml-2">{m.email}</span>
                                  </div>
                                  <span className="text-[10px] uppercase ml-2 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{m.role}</span>
                                  {m.unscoped && !m.source && (
                                    <span className="text-[10px] uppercase ml-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-600" title="Admin — implicit access to every org">ALL ORGS</span>
                                  )}
                                  {m.source === 'linked_tenant' && (
                                    <span className="text-[10px] uppercase ml-1 px-1.5 py-0.5 rounded bg-teal-100 text-teal-600">linked</span>
                                  )}
                                  {Array.isArray(m.company_access_for_org) && m.company_access_for_org.length > 0 && (
                                    <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title={m.company_access_for_org.join(', ')}>
                                      {m.company_access_for_org.length} co.
                                    </span>
                                  )}
                                </div>
                                {!m.unscoped && !m.source && (
                                  <button onClick={() => handleRemoveMember(org.org_id, m.user_id)} className="text-xs text-red-500 hover:text-red-700" data-testid={`remove-member-${m.user_id}`}>Remove</button>
                                )}
                              </div>
                            ))}
                            {(() => {
                              const memberIds = new Set(members[org.org_id].map(m => m.user_id));
                              const nonMembers = allUsers.filter(u => !memberIds.has(u.user_id) && !['admin', 'super_admin'].includes(u.role));
                              if (nonMembers.length === 0) return null;
                              return (
                                <div className="mt-2">
                                  <p className="text-xs text-gray-400 mb-1">Add to this org:</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {nonMembers.map(u => (
                                      <button key={u.user_id} onClick={() => handleAddMember(org.org_id, u.user_id)} className="px-2.5 py-1 text-xs bg-white border rounded hover:border-blue-400 hover:text-blue-600" data-testid={`add-member-${u.user_id}`}>
                                        + {u.name || u.email}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Unassigned companies */}
        {unassignedCompanies.length > 0 && (
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="unassigned-companies">
            <p className="text-sm font-semibold text-amber-800 mb-2">{unassignedCompanies.length} Unassigned {unassignedCompanies.length === 1 ? 'Company' : 'Companies'}</p>
            <p className="text-xs text-amber-600 mb-3">These companies are not assigned to any organization.</p>
            <div className="flex flex-wrap gap-2">
              {unassignedCompanies.map(c => (
                <span key={c.name} className="px-2 py-1 bg-white border border-amber-200 rounded text-xs text-amber-800">{c.name}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function InviteRow({ inv, onAction, onRevoke }) {
  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    revoked: 'bg-gray-100 text-gray-500',
  };
  const statusLabel = { pending: 'Pending', accepted: 'Accepted', rejected: 'Rejected', revoked: 'Revoked' };
  const isJoinRequest = inv.type === 'join_request';

  return (
    <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border" data-testid={`invite-row-${inv.invite_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-800 truncate">{inv.email}</span>
          <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded font-medium ${statusColors[inv.status]}`}>
            {isJoinRequest ? 'Join Request' : statusLabel[inv.status]}
          </span>
          {inv.type === 'email' && <Mail className="w-3 h-3 text-gray-300" />}
          {isJoinRequest && <UserPlus className="w-3 h-3 text-amber-400" />}
        </div>
        {inv.company_name && <p className="text-[11px] text-gray-400 mt-0.5">Company: {inv.company_name}</p>}
        <p className="text-[11px] text-gray-300">{new Date(inv.created_at).toLocaleDateString()}</p>
      </div>
      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
        {inv.status === 'pending' && isJoinRequest && (
          <>
            <button onClick={() => onAction(inv.invite_id, 'approve')} className="px-2 py-1 text-[11px] font-medium bg-green-100 text-green-700 rounded hover:bg-green-200" data-testid={`approve-invite-${inv.invite_id}`}>Approve</button>
            <button onClick={() => onAction(inv.invite_id, 'reject')} className="px-2 py-1 text-[11px] font-medium bg-red-100 text-red-700 rounded hover:bg-red-200" data-testid={`reject-invite-${inv.invite_id}`}>Reject</button>
          </>
        )}
        {inv.status === 'pending' && !isJoinRequest && (
          <button onClick={() => onRevoke(inv.invite_id)} className="px-2 py-1 text-[11px] font-medium bg-gray-100 text-gray-600 rounded hover:bg-gray-200" data-testid={`revoke-invite-${inv.invite_id}`}>Revoke</button>
        )}
      </div>
    </div>
  );
}
